import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  fetchAssesseeReports,
  fetchAssessees,
  fetchAssessors,
  linkAssesseeToAssessments,
} from "reduxStore/actions/assessmentActions";
import { fetchDivisionForOrganisation } from "reduxStore/actions/divisionActions";
import { fetchLevelsForOrganisation } from "reduxStore/actions/levelActions";
import { fetchAllLocations } from "reduxStore/actions/locationActions";
import {
  checkRoles,
  getPermissionFlags,
  getUserScope,
} from "utils/permissions";
import { getCoachingColumns } from "../components/columns";
import { useNavigate } from "react-router-dom";

export const useCoaching = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const isLargeScreen = useMediaQuery("(min-width:1000px)");

  const user = useSelector((state) => state.user);
  const {
    organisationId: scopedOrg,
    locationIds,
    divisionIds,
    levelIds,
  } = useMemo(() => getUserScope(user), [user]);
  const userOrg =
    scopedOrg || user?.organisation?._id || user?.organisation || "";
  const defaultLocation = locationIds[0] || "";
  const defaultDivision = divisionIds[0] || "";
  const defaultLevel = levelIds[0] || "";
  const filtersInitialized = useRef(false);
  const { showOrg, showLocation, showDivision, showLevel, showSeniorLeader } =
    getPermissionFlags(user);
  const { isAcademyImprovementLead } = checkRoles(user);

  const [searchInput, setSearchInput] = useState("");
  const initialLocation =
    showOrg || isAcademyImprovementLead ? "" : defaultLocation;
  const initialDivision =
    showOrg || isAcademyImprovementLead ? "" : defaultDivision;
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [selectedDivision, setSelectedDivision] = useState(initialDivision);
  const [selectedLevel, setSelectedLevel] = useState(defaultLevel);
  const [selectedAssessor, setSelectedAssessor] = useState("");
  const [selectedAssessee, setSelectedAssessee] = useState("");
  const [startDate, setStartDate] = useState(dayjs().subtract(12, "month"));
  const [endDate, setEndDate] = useState(dayjs());

  const rawLocations = useSelector((state) => state.locationsAll);
  const rawDivisions = useSelector((state) => state.divisionsByLocation);
  const rawLevels = useSelector((state) => state.levelsForOrganisation);
  const locations = useMemo(() => rawLocations || [], [rawLocations]);
  const divisions = useMemo(() => rawDivisions || [], [rawDivisions]);
  const levels = useMemo(() => rawLevels || [], [rawLevels]);
  const assessors = useSelector((state) => state.assessors) || [];
  const assesseeNames = useSelector((state) => state.assessees) || [];

  // State for controlling rows per page
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const [sortModel, setSortModel] = useState([
    { field: "createdAt", sort: "desc" }, // Default sorting by createdAt
  ]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const assesseeReports = useSelector((state) => state.assesseeReports);
  const rowsData = assesseeReports?.data;
  const rows = useMemo(() => rowsData || [], [rowsData]);
  const totalRows = assesseeReports?.total || 0;

  // new user admin dialog form
  const [openUserForm, setOpenUserForm] = useState(false);
  const [currentUserName, setCurrentUserName] = useState(null);
  const [newUserId, setNewUserId] = useState(null);

  const handleOpenUserForm = useCallback(
    (row) => {
      const userName = row?.name || "";
      const school = row?.school || "";
      const [firstName, lastName] = userName.split(" ");

      if (!lastName) {
        console.warn("No last name provided, using 'Smith' as default.");
        return handleOpenUserForm({ ...row, name: `${firstName} Smith` });
      }

      if (!firstName || !lastName) {
        console.error("Invalid userName format:", userName);
        return;
      }

      const existingLinked = rows.find(
        (r) => r.name === userName && r.linked && r.school === school
      );
      const unlinkedRow = rows.find(
        (r) => r.name === userName && !r.linked && r.school === school
      );

      // If a linked user already exists with the same name and location, just link the orphan assessments.
      if (existingLinked && unlinkedRow) {
        setCurrentUserName({ firstName, lastName, school });
        setNewUserId(existingLinked.id);
        return;
      }

      setCurrentUserName({ firstName, lastName, school });
      setOpenUserForm(true);
    },
    [rows]
  );

  // handle navigate to user's coaching feed
  const handleNavigateToUserFeed = useCallback(
    (userId) => {
      if (userId) {
        navigate(`/coaching-feed/${userId}`);
      } else {
        console.error("No userId provided for navigation.");
      }
    },
    [navigate]
  );

  useEffect(() => {
    const linkAssessmentsToUser = async () => {
      if (!newUserId || !currentUserName) return;

      const fullName = `${currentUserName.firstName} ${currentUserName.lastName}`;
      const targetSchool = currentUserName.school || "";
      const unlinkedRow = rows.find(
        (row) =>
          row.name === fullName && row.school === targetSchool && !row.linked
      );
      const matchingRow =
        unlinkedRow ||
        rows.find(
          (row) =>
            row.name === fullName &&
            (row.school === targetSchool || !targetSchool)
        );
      const assesseeId = newUserId;

      if (matchingRow && matchingRow.assessmentIds.length > 0) {
        const result = await dispatch(
          linkAssesseeToAssessments(assesseeId, matchingRow.assessmentIds)
        );

        if (result?.success) {
          console.log(
            `✅ Linked ${result.modifiedCount} assessments to ${fullName}`
          );
          setRefreshKey((prev) => prev + 1);
        } else {
          console.warn(
            "⚠️ Failed to link assessments:",
            result?.message || result?.error
          );
        }
      } else {
        console.info(`ℹ️ No assessments to link for ${fullName}`);
      }

      // Reset state
      setNewUserId(null);
    };

    linkAssessmentsToUser();
  }, [newUserId, currentUserName, rows, dispatch]);

  const handleCloseUserForm = useCallback(() => {
    setOpenUserForm(false);
    setCurrentUserName(null);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [
    selectedLocation,
    selectedDivision,
    selectedLevel,
    selectedAssessor,
    selectedAssessee,
    startDate,
    endDate,
    search,
  ]);

  // keep defaults in sync if user scope changes
  useEffect(() => {
    if (filtersInitialized.current) return;
    const hasOptions =
      (locations && locations.length) ||
      (divisions && divisions.length) ||
      (levels && levels.length);
    if (!hasOptions) return;

    if (
      !selectedLocation &&
      defaultLocation &&
      !(showOrg || isAcademyImprovementLead)
    ) {
      setSelectedLocation(defaultLocation);
    }
    if (
      !selectedDivision &&
      defaultDivision &&
      !(showOrg || isAcademyImprovementLead)
    ) {
      setSelectedDivision(defaultDivision);
    }
    if (!selectedLevel && defaultLevel) {
      setSelectedLevel(defaultLevel);
    }
    filtersInitialized.current = true;
  }, [
    defaultLocation,
    defaultDivision,
    defaultLevel,
    selectedLocation,
    selectedDivision,
    selectedLevel,
    locations,
    divisions,
    levels,
    showOrg,
    isAcademyImprovementLead,
  ]);

  // Ensure selected filters are valid against available options
  useEffect(() => {
    const locIds = new Set((locations || []).map((l) => l._id));
    if (selectedLocation && !locIds.has(selectedLocation)) {
      setSelectedLocation("");
    } else if (!selectedLocation && locIds.size === 1) {
      setSelectedLocation([...locIds][0]);
    }

    const divIds = new Set((divisions || []).map((d) => d._id));
    if (selectedDivision && !divIds.has(selectedDivision)) {
      setSelectedDivision("");
    } else if (!selectedDivision && divIds.size === 1) {
      setSelectedDivision([...divIds][0]);
    }

    const lvlIds = new Set((levels || []).map((l) => l._id));
    if (selectedLevel && !lvlIds.has(selectedLevel)) {
      setSelectedLevel("");
    } else if (!selectedLevel && lvlIds.size === 1) {
      setSelectedLevel([...lvlIds][0]);
    }
  }, [
    locations,
    divisions,
    levels,
    selectedLocation,
    selectedDivision,
    selectedLevel,
  ]);

  // Define the columns for the DataGrid
  const columns = getCoachingColumns(
    primaryDark,
    handleOpenUserForm,
    handleNavigateToUserFeed
  );

  useEffect(() => {
    if (userOrg) {
      dispatch(
        fetchAssesseeReports({
          page,
          limit: pageSize,
          search,
          organisation: userOrg,
          location:
            showOrg || isAcademyImprovementLead
              ? selectedLocation
              : selectedLocation || defaultLocation,
          division:
            showLevel || showSeniorLeader || isAcademyImprovementLead
              ? selectedDivision
              : selectedDivision || defaultDivision,
          level:
            showDivision || showSeniorLeader || isAcademyImprovementLead
              ? selectedLevel
              : selectedLevel || defaultLevel,
          assessor: selectedAssessor,
          assessee: selectedAssessee,
          startDate: startDate ? startDate.toISOString() : undefined,
          endDate: endDate ? endDate.toISOString() : undefined,
          sort: sortModel.length > 0 ? sortModel[0].field : "dateOfAssessment", // Default to createdAt if no sort model
          order: sortModel.length > 0 ? sortModel[0].sort : "desc", // Default to descending order
          filters: filterModel,
        })
      );
    }
  }, [
    dispatch,
    userOrg,
    page,
    pageSize,
    search,
    sort,
    sortModel,
    showOrg,
    showLocation,
    showDivision,
    showLevel,
    showSeniorLeader,
    selectedLocation,
    selectedDivision,
    selectedLevel,
    selectedAssessor,
    selectedAssessee,
    startDate,
    endDate,
    filterModel,
    isAcademyImprovementLead,
    refreshKey,
    defaultLocation,
    defaultDivision,
    defaultLevel,
  ]);

  useEffect(() => {
    dispatch(fetchAllLocations());

    dispatch(
      fetchDivisionForOrganisation({
        organisationId: userOrg,
        locationId: showOrg
          ? selectedLocation
          : selectedLocation || defaultLocation,
      })
    );

    dispatch(
      fetchLevelsForOrganisation({
        organisationId: userOrg,
        locationId: showOrg
          ? selectedLocation
          : selectedLocation || defaultLocation,
      })
    );

    dispatch(
      fetchAssessors({
        organisation: userOrg,
        location: showOrg
          ? selectedLocation
          : selectedLocation || defaultLocation,
      })
    );

    dispatch(
      fetchAssessees({
        organisation: userOrg,
        location: showOrg
          ? selectedLocation
          : selectedLocation || defaultLocation,
      })
    );
  }, [dispatch, showOrg, selectedLocation, defaultLocation, userOrg]);

  return {
    backgroundAlt,
    primaryLight,
    primaryDark,
    isLargeScreen,
    showOrg,
    showDivision,
    showLevel,
    showSeniorLeader,
    isAcademyImprovementLead,
    searchInput,
    setSearchInput,
    selectedLocation,
    setSelectedLocation,
    selectedDivision,
    setSelectedDivision,
    selectedLevel,
    setSelectedLevel,
    selectedAssessor,
    setSelectedAssessor,
    selectedAssessee,
    setSelectedAssessee,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    locations,
    divisions,
    levels,
    assessors,
    assesseeNames,
    pageSize,
    setPageSize,
    page,
    setPage,
    search,
    setSearch,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    sort,
    setSort,
    rows,
    totalRows,
    columns,
    openUserForm,
    setOpenUserForm,
    currentUserName,
    handleCloseUserForm,
    setNewUserId,
  };
};
